import type { SafeResourceUrl } from '@angular/platform-browser';
import {LanguageConfig} from './language-config';

export interface AppConfiguration {
    wifiPrefixUrl: string;
    shouldUpgradeESP: boolean;
    shouldUpgrade: boolean;
    appName: string;
    updateUser: string;
    updatePassword: string;
    updateUrl: string;
    upgradeUser: string;
    upgradePassword: string;
    product: string;
    validProducts: string[];
    defaultLang: string;
    resultBaseUrl: string;
    measurementUrl: string;
    measurementHrvUrl: string;
    vwsBaseUrls: string[];
    vwsPrerequestUrl: string;
    vwsMeasurementUrl: string;
    testConnectionUrl: string;
    dataPrivacyUrl: string;
    // measurements: MeasurementConfig[];
    languages: LanguageConfig[];
    firmware_hw1_version: string;
    firmware_hw1_filename: string;
    firmware_hw2_version: string;
    firmware_hw2_filename: string;
    firmware_hw3_version: string;
    firmware_hw3_filename: string;
    firmware_hw4_version: string;
    firmware_hw4_filename: string;
    firmware_hw2_version_old: string;
    firmware_hw2_filename_old: string;
    firmware_hw3_version_old: string;
    firmware_hw3_filename_old: string;
    firmware_hw4_version_old: string;
    firmware_hw4_filename_old: string;
    firmware_10_version: string;
    firmware_10_filename: string;
    firmware_20_version: string;
    firmware_20_filename: string;
    firmware_nuvoton_version: string;
    firmware_nuvoton_filename: string;
    sensor_version: number;
    sensor_filename: string;
}

export interface CurrentSettings {
    handRecognitionLimitDarkUpper?: number;
    handRecognitionLimitBrightLower?: number;
    handRecognitionLimitBrightUpper?: number;
    batteryCutoffVoltage?: number;
    batteryPrewarningVoltage?: number;
}


interface AppTimeout {
    timer: number;
    onExpiration: string;
}

interface CopyParameter {
  originGroup: string;
  originKey: string;
  targetGroup: string;
  targetKey: string;
}

interface ExportParameters {
    key: string;
    caption: string;
}

interface ResultTableParameters {
    type: string;
    key: string;
    caption: string;
}

interface SplitParameter {
    originGroup: string;
    originKey: string;
    targetGroup: string;
    targetKey: string;
    splitCharacter: string;
}

interface SetParameter {
    newValue: string | boolean | number;
    newValueType: string;
    targetGroup: string;
    targetKey: string;
    inputKey1?: string;
    inputGroup1?: string;
    inputKey2?: string;
    inputGroup2?: string;
    min?: number;
    max?: number;
    isInt?: boolean;
    accuracy?: number;
    searchValue?: string;
    replacementValue?: string;
    sliceStart?: number;
    sliceEnd?: number;
}

interface CommandParameters {
    commandName: string;
    transferConstant?: any;
    toResKey?: string;
    toResGroup?: string;
    fromResKey?: string;
    fromResGroup?: string;
}

interface SwitchParameter {
    switchVal: string;
    switchTarget: string;
}

interface LogParameters {
  message: string;
}

export interface AppSettings {
  useTemplate?: string;
  templateName?: string;
  codeType: string;
  codeFrontCamera?: boolean;
  codeEnableFlip?: boolean;
  additionalData: any;
  eventTargets?: {[eventName: string]: string};
  useDesign: string;
  timeout?: AppTimeout;
  callFunction?: string;
  appVersion?: string;
  beta?: boolean;
  appName?: 'VitalityCheck' | 'VEGEcheck';
  mandatory?: boolean;
  functionParameters?: {
    key: string,
    value: string,
    condition: string,
    group: string,
    keys?: string
  };
  logParameters?: LogParameters;
  copyParameters?: CopyParameter[];
  exportParameters?: ExportParameters[];
  exportDelimiter?: string;
  resultTableParameters?: ResultTableParameters[];
  splitParameters?: SplitParameter;
  setParameters?: SetParameter[];
  commandParameters?: CommandParameters;
  switchParameters?: SwitchParameter[];
  resKey: string;
  resGroup: string;
  thresholdHistoryResKey?: string;
  thresholdHistoryResGroup?: string;
  sortedRelevantThresholdHistoryResKey?: string;
    thresholdHistoryRemappedResKey?: string;
  postURL?: string;
  pingURL?: string;
  getURL?: string;
  getKey?: string;
  getGroup?: string;
  postKey?: string;
  postGroup?: string;
  /** Optional per-stage target for generated result URLs. */
  resultBaseUrl?: string;
  partialResult?: boolean;
  resetConfig?: boolean;
  additionalArgument?: string;
  validateSerial?: boolean;
  burnTheEvidence?: boolean;
}

interface Background {
    image?: string;
    width?: number;
    height?: number;
    sectionWidth?: number;
    sectionHeight?: number;
    sectionX?: number;
    sectionY?: number;
    hasBorder?: boolean;
    muted?: boolean;
}

export interface NumberAnimationOptions {
    /** Value displayed at the beginning of the animation. Defaults to 0. */
    startValue?: number;
    /** Animation duration in milliseconds. Defaults to 1000. */
    duration?: number;
    /** Number of digits displayed after the decimal separator. */
    decimals?: number;
}

export interface ColorMappingStop {
    /** Absolute position on the source value scale. */
    value: number;
    /** Six-digit hexadecimal color, for example #D83A3A. */
    color: string;
}

export interface ColorMapping {
    /** Inclusive lower bound of the source value range. */
    min: number;
    /** Inclusive upper bound of the source value range. */
    max: number;
    mode: 'linear' | 'discrete';
    /** CSS property receiving the calculated color. Defaults to background-color. */
    cssProperty?: string;
    stops: ColorMappingStop[];
}

export interface ValueSource {
    /** State group; omit for values directly below the root state object. */
    group?: string;
    key: string;
}

export interface ElementDesign {
    maintitle?: string;
    useTemplate?: string;
    templateName?: string;
    deletedKeys?: [string];
    elementType: string;
    subtitle?: string;
    css?: {
        [key: string]: string
    };
    goTo?: string;
    url?: string;
    handleFunction?: {
        isRadio?: boolean;
        buttonGroup?: string,
        name: string,
        value?: string,
        group?: string,
        elementType?: string,
        argument?: string,
        init?: boolean
    };
    condition?: {
        /** Delay visibility for the configured number of seconds. */
        displayAfter?: number,
        checkIf: string,
        group: string,
        equalsValue: string
    };
    design?: string;
    trustedResourceUrl?: SafeResourceUrl;
    background?: Background;
    ignoreAdjust?: boolean;
    keepActive?: boolean;
    resource?: string;
    index?: number;
    innerSize?: {
        width: string,
        height: string,
        x_key?: string,
        x_keyGroup?: string,
        x_min?: number,
        x_max?: number,
        y_key?: string,
        y_keyGroup?: string,
        y_min?: number,
        y_max?: number,
    };
    cssInner?: Record<string, string>;
    move?: string;
    class?: string;
    focus?: boolean;
    numberAnimation?: NumberAnimationOptions;
    /** Source value used to calculate a valueMappedColor element's color. */
    valueSource?: ValueSource;
    /** Configurable mapping used by a valueMappedColor element. */
    colorMapping?: ColorMapping;
}

export interface AppDesign extends Record<string, any>{
    elements: {
        [element: string]: ElementDesign
    };
    background?: string;
    backgroundType?: string;
    backgroundSize?: {
        width?: number,
        height?: number,
        sectionWidth?: number,
        sectionHeight?: number,
        sectionX?: number,
        sectionY?: number,
        hasBorder?: boolean,
        scaleMode?: string,
    };
    backgroundBounds?: {
        minWidth?: number,
        minHeight?: number,
        maxWidth?: number,
        maxHeight?: number,
        goTo?: string
    };
    orientation?: {
        lock?: boolean,
        value?: string,
    };
    scroll?: {
        scrollX?: boolean,
        scrollY?: boolean,
    };
    template?: string;
    heading?: string;
    absolute?: boolean;
    awaitVideo?: boolean;
    virtualKeyboard?: boolean;
}
export interface AppConfig {
    design: AppDesign;
    settings: AppSettings;
    deletedKeys?: [string];
}


export interface AppConfigs {
    configs: {[stageName: string]: AppConfig};
    settings: {
        numMeasurementsAox?: number,
        homeStage: string,
        initialStage: string,
        languageStage?: string,
        updateStage?: string,
        usbStage?: string,
        permissionStage?: string,
        appParameters?: Record<string, any>;
        targetAPI?: string,
        calcURLs?: string,
        askForUSB: boolean,
        askForSerial: boolean,
        virtualKeyboard?: boolean,
    };
    language?: Record<string, Record<string, string>>;
    templates?: Record<string, AppConfig>;
    templatesElements?: Record<string, ElementDesign>;
    designTag: string;
    timestamp: number;
}

export enum State {
    SCANNER_KEY = 'SCANNER_KEY',
    SCANNER_SERIAL = 'SCANNER_SERIAL',
    SCANNER_VERSION = 'SCANNER_VERSION',
    SELECTED_LANGUAGE = 'SELECTED_LANGUAGE',
    LANGUAGE_TAG = 'LANGUAGE_TAG',
    LANGUAGE = 'LANGUAGE',
    DEMO_MODE = 'DEMO_MODE',
    OS = 'OS',
    INITIALIZED = 'INITIALIZED',
    RESULT = 'RESULT',
    REFERRER = 'REFERRER',
    ERROR_CODE = 'ERROR_CODE',
    AOX_MEASUREMENTS_TO_RUN = 'AOX_MEASUREMENTS_TO_RUN',
    AOX_MEASUREMENT_RUNS = 'AOX_MEASUREMENT_RUNS',
    AOX_MEASUREMENT_CYCLE = 'AOX_MEASUREMENT_CYCLE',
    AOX_MEASUREMENT_MULTI = 'AOX_MEASUREMENT_MULTI ',
    AOX_MEASUREMENT_MULTI_LAST_RUN = 'AOX_MEASUREMENT_MULTI_LAST_RUN',
    RESULT_PAGE_URL = 'RESULT_PAGE_URL',
    AOX_MEASUREMENT_RUNNING = 'AOX_MEASUREMENT_RUNNING',
    ADDITIONAL_DATA_KEYS = 'ADDITIONAL_DATA_KEYS',
    HRV_MEASUREMENT_RUNNING = 'HRV_MEASUREMENT_RUNNING',
    SCANNER_WIFI = 'SCANNER_WIFI',
    PROFILE_DATA = 'PROFILE_DATA',
    GUEST_DATA = 'GUEST_DATA',
    PROFILE_CHOSEN = 'PROFILE_CHOSEN',
    AOX_MEASUREMENT_RESULTS = 'AOX_MEASUREMENT_RESULTS',
    SKIP_PRIVACY = 'SKIP_PRIVACY',
    VERSION = 'VERSION',
    REDUCE_WIFI_QUERYS = 'REDUCE_WIFI_QUERYS',
    AOX_MEASUREMENTS = 'AOX_MEASUREMENTS',
    LAST_VWS_INDEX = 'LAST_VWS_INDEX',
    SCANNER_NEEDS_POWER_OFF = 'SCANNER_NEEDS_POWER_OFF',
    HRV_FORCE_ONLINE = 'HRV_FORCE_ONLINE',
    USB_MODE = 'USB_MODE',
    SKIP_WAIT_FOR_CONNECTION = 'SKIP_WAIT_FOR_CONNECTION',
    SCANNER_STATUS = 'SCANNER_STATUS',
    CONNECTION_TYPE = 'CONNECTION_TYPE',
    APP_VERSION = 'APP_VERSION',
    HRV_MEASUREMENT_RESULT = 'HRV_MEASUREMENT_RESULT',
    profile = 'profile',
    CONNECTION_PARAMS = 'CONNECTION_PARAMS',
    DEBUG_MODE = 'DEBUG_MODE',
    PLATFORM = 'PLATFORM',
    IS_WEBAPP = 'IS_WEBAPP',
    ALLOW_HRV = 'ALLOW_HRV',
    USERNAME = 'USERNAME',
    JWT = 'JWT',
    INDEX = 'INDEX',
    acceptedURLs = 'ACCEPTED_URLS',
    HAND_RECOGNITION_DATA = 'HAND_RECOGNITION_DATA',
    CONFIG_NAME = 'CONFIG_NAME',
    MEASUREMENT_PROGRESS = 'MEASUREMENT_PROGRESS',
    MEASUREMENT_PROGRESS_PERCENT = 'MEASUREMENT_PROGRESS_PERCENT',
    LICENSE = 'LICENSE',
    RESULT_AVG = 'RESULT_AVG',
    virtualKeyboard = 'virtualKeyboard',
    SCANNER_MODEL = 'SCANNER_MODEL',
    measurementErrorThreshold = 'measurementErrorThreshold',
}

export interface StateInterface extends Partial<Record<keyof typeof State, any>>{
    /** Timeout for AOX VWS pre-requests in milliseconds. Defaults to 10 seconds. */
    vwsPreRequestTimeoutMs?: number;
    /** Number of maximum-timeout VWS pre-request rounds. Defaults to one. */
    vwsPreRequestMaxTimeoutAttempts?: number;
    /** Number of additional AOX VWS upload retries after the initial request. Defaults to five. */
    vwsUploadMaxRetries?: number;
    /** Number of additional transparent HRV upload retries after the initial request. Defaults to three. */
    hrvUploadMaxRetries?: number;
}

export interface GeneralState {
    persistentValues: StateInterface;
    [dict: string]: Record<string, any>;
}
